// @givmo/app-admin — two-factor code prompt (AS-WEB1).
//
// An admin who is also a consumer may have enrolled TOTP on the Givmo mobile app (web ships no
// enrolment). Their password/OAuth first factor then succeeds and GCIP demands a second factor, which
// @givmo/auth hands back as a `TotpChallenge` on the thrown error. This renders that challenge as a
// real STATE of the sign-in screen — the form is replaced in place, and completing the code resumes
// the very flow that was interrupted (the challenge carries its own tail, so a challenged sign-in
// lands exactly where a clean one would). Rendered by BOTH sign-in entry points, since either can be
// challenged. There is always a way back to the form: an enrolled admin must never be stranded.
//
// SINGLE-USE CODES. A TOTP code is valid once per 30-second window; a spent code is rejected and only
// the next window's code works. So a rejection CLEARS the field rather than leaving the code sitting
// there for a second submit, and re-typing an already-spent code keeps Verify disabled — the retry is
// structurally forced to be a fresh code, not a resubmission of the failed one.
//
// Drawn in the register of the portal's sign-in chooser, whose screen it replaces in place (EP-WEB43).

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Button, OtpField, ErrorMessage, testIdProp } from '@givmo/design-system';
import { SignInDoorError, type SignInDoorErrorProps } from '@givmo/passkeys';
import type { AdminSignInResult, TotpChallenge } from '@givmo/auth';
import { useAuthError, type AuthDoorKey } from './use-auth-error';
import { landSignInWithoutCompany } from './companyless-landing';

const CODE_LENGTH = 6;
const CODE_REJECTED_MESSAGE =
  'That code was already used or has expired — enter the current code from your authenticator app.';
const CODE_ALREADY_TRIED_MESSAGE =
  'You already used that code. Wait for your authenticator app to show the next one.';

export interface TotpChallengePromptProps {
  /**
   * The door rendering this prompt (EP-SUITE2-FIX6). Completing the code SIGNS IN, and the identity
   * boundary then remounts the door without the prompt, so a failure after that is surfaced into the
   * door's own error slot, where the remounted door renders it.
   */
  door: AuthDoorKey;
  /** The pending challenge, read off the caught sign-in failure with `secondFactorChallengeOf`. */
  challenge: TotpChallenge<AdminSignInResult>;
  /** Called with the completed sign-in result — the same shape an unchallenged sign-in returns. */
  onResolved: (result: AdminSignInResult) => void | Promise<void>;
  /** Abandon the challenge and return to the sign-in form. */
  onCancel: () => void;
  /**
   * Opens the way back in (AS-WEB27): the company's sign-in lock can refuse a sign-in as its code
   * completes it, and then the prompt shows the lock notice. Absent, the refusal is its plain line.
   */
  onUnlink?: () => void;
  /** The lock notice's organization door, where the page offers one (AS-WEB30). */
  organizationSso?: SignInDoorErrorProps['organizationSso'];
}

export function TotpChallengePrompt({
  door,
  challenge,
  onResolved,
  onCancel,
  onUnlink,
  organizationSso,
}: TotpChallengePromptProps) {
  const { error, lockRefused, clearError, surface } = useAuthError(door);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rejected, setRejected] = useState(false);
  // Codes this challenge has already spent. State, not a ref: it is read during render to gate the
  // Verify button, and a ref read during render is not safe under concurrent rendering.
  const [spentCodes, setSpentCodes] = useState<ReadonlySet<string>>(() => new Set());

  const alreadyTried = spentCodes.has(code);

  const onVerify = async () => {
    if (code.length !== CODE_LENGTH || alreadyTried) return;
    clearError();
    setRejected(false);
    setSubmitting(true);
    try {
      const result = await challenge.resolve(code);
      await onResolved(result);
    } catch (thrown) {
      // Spend the code and clear the field: the next attempt cannot be this same code.
      setSpentCodes((spent) => new Set(spent).add(code));
      setCode('');
      // EP-SUITE2-FIX5: the code was accepted and the tail found no company — the account is signed
      // in, so land it, as an unchallenged sign-in's no-company tail is.
      const landing = await landSignInWithoutCompany(thrown, queryClient);
      if (landing !== null) {
        await navigate(landing);
        return;
      }
      const authError = surface(thrown);
      if (authError.kind === 'invalidVerificationCode') setRejected(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-20" {...testIdProp('auth-totp-challenge')}>
      <div className="flex flex-col gap-4">
        <h1 className="font-sans text-s22 font-w700 tracking-givmo text-text-strong">
          Two-factor authentication
        </h1>
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {challenge.factorDisplayName !== null
            ? `Enter the six-digit code from ${challenge.factorDisplayName} to finish signing in.`
            : 'Enter the six-digit code from your authenticator app to finish signing in.'}
        </p>
      </div>

      <OtpField
        length={CODE_LENGTH}
        value={code}
        onValueChange={setCode}
        error={rejected || alreadyTried}
        aria-label="Two-factor authentication code"
        testId="auth-totp-code"
      />
      {rejected && <ErrorMessage testId="auth-totp-error">{CODE_REJECTED_MESSAGE}</ErrorMessage>}
      {alreadyTried && (
        <ErrorMessage testId="auth-totp-reused">{CODE_ALREADY_TRIED_MESSAGE}</ErrorMessage>
      )}
      {/* Anything that isn't a rejected code — an expired challenge, a network failure — shows its
          own message; "Back to sign in" is the way out of every one of them. */}
      {!rejected && (
        <SignInDoorError
          error={error}
          lockRefused={lockRefused}
          onUnlink={onUnlink}
          organizationSso={organizationSso}
        />
      )}

      <Button
        appearance="saas"
        tone="primary"
        size="lg"
        fullWidth
        loading={submitting}
        disabled={code.length !== CODE_LENGTH || submitting || alreadyTried}
        onClick={() => void onVerify()}
        testId="auth-totp-verify"
      >
        Verify
      </Button>

      <button
        type="button"
        onClick={onCancel}
        className="self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
        {...testIdProp('auth-totp-cancel')}
      >
        Back to sign in
      </button>
    </div>
  );
}
